import React, { useEffect, useState } from 'react';
import CustomInput from '../../Components/CommonComponents/CustomInput';
import CustomSpinner from '../../Components/CommonComponents/CustomSpinner';
import { toast, ToastContainer } from 'react-toastify';
import { getJwtToken, END_POINT } from '../../Redux/AdminReducer/action';
import axios from 'axios';
import ModalPortal, { MODAL_Z } from '../../Components/CommonComponents/ModalPortal';

const initialFormData = {
  nameOfMedicine: '',
  company: '',
  potencyOrPower: '',
  quantity: '',
  typeOfMedicine: '',
  distributorName: '',
  package: '',
  expiryDate: '',
  minQuantity: '',
  maxQuantity: '',
  mrp: '',
  discount: '',
  hsnCode: '',
  gst: '',
  packagingSize: '',
  mafDate: '',
  orderId: '',
  invoiceNo: '',
};

// Optional numeric fields - validated only when the user actually types something
const NUMBER_FIELDS = [
  { key: 'package', label: 'Package' },
  { key: 'minQuantity', label: 'Minimum Quantity' },
  { key: 'maxQuantity', label: 'Maximum Quantity' },
  { key: 'mrp', label: 'MRP' },
  { key: 'discount', label: 'Discount' },
  { key: 'gst', label: 'GST %' },
];

export default function MedicineModal({ isOpen, onClose, itemData, onSuccess }) {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (itemData) {
      setFormData({
        ...initialFormData,
        ...itemData,
        expiryDate: itemData.expiryDate ? itemData.expiryDate.slice(0, 10) : '',
        mafDate: itemData.mafDate ? itemData.mafDate.slice(0, 10) : '',
      });
    } else {
      setFormData(initialFormData);
    }
    setErrors({});
  }, [itemData, isOpen]);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));
  };

  // Fields the backend schema marks required, plus basic sanity checks
  const validate = () => {
    const newErrors = {};

    if (!String(formData.nameOfMedicine || '').trim()) {
      newErrors.nameOfMedicine = 'Name of Medicine is required';
    }

    if (String(formData.quantity ?? '').trim() === '') {
      newErrors.quantity = 'Quantity is required';
    } else if (isNaN(Number(formData.quantity)) || Number(formData.quantity) < 0) {
      newErrors.quantity = 'Quantity must be a valid number (0 or more)';
    }

    NUMBER_FIELDS.forEach(({ key, label }) => {
      const raw = String(formData[key] ?? '').trim();
      if (raw === '') return;
      if (isNaN(Number(raw)) || Number(raw) < 0) {
        newErrors[key] = `${label} must be a valid number (0 or more)`;
      }
    });

    const min = Number(formData.minQuantity);
    const max = Number(formData.maxQuantity);
    if (!newErrors.minQuantity && !newErrors.maxQuantity &&
        String(formData.minQuantity ?? '').trim() !== '' &&
        String(formData.maxQuantity ?? '').trim() !== '' &&
        min > max) {
      newErrors.maxQuantity = 'Maximum Quantity cannot be less than Minimum Quantity';
    }

    if (!newErrors.discount && String(formData.discount ?? '').trim() !== '' && Number(formData.discount) > 100) {
      newErrors.discount = 'Discount cannot be more than 100';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    const newErrors = validate();
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      toast.error('Please fill the highlighted required fields');
      return;
    }
    setErrors({});

    // Mongoose casts '' to NaN/Invalid Date for Number & Date fields, so drop blanks
    const payload = { ...formData };
    [...NUMBER_FIELDS.map((f) => f.key), 'quantity', 'expiryDate', 'mafDate'].forEach((key) => {
      if (String(payload[key] ?? '').trim() === '') delete payload[key];
    });

    setIsProcessing(true);
    try {
      if (formData._id) {
        await axios.put(`${END_POINT}/inventory/inventory/${formData._id}`, payload, {
          headers: { Authorization: getJwtToken() },
        });
        toast.success('Medicine Details Updated');
      } else {
        await axios.post(`${END_POINT}/inventory/inventory`, payload, {
          headers: { Authorization: getJwtToken() },
        });
        toast.success('Medicine Added Successfully');
      }
      setIsProcessing(false);
      if (onSuccess) {
        onSuccess();
      } else {
        setTimeout(() => window.location.reload(), 500);
      }
      if (onClose) onClose();
    } catch (error) {
      setIsProcessing(false);
      const data = error?.response?.data;
      const message =
        (typeof data === 'string' && data) ||
        data?.error ||
        data?.message ||
        error?.message ||
        'Failed to save medicine';
      toast.error(message);
    }
  };

  return (
    <ModalPortal onClose={onClose}>
    <div className={`fixed inset-0 ${MODAL_Z} flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 overflow-y-auto`}>
      <div className="relative w-full max-w-4xl rounded-2xl bg-white p-6 shadow-2xl my-8 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 border-l-4 border-blue-500 pl-3">
            {formData?._id ? 'Modify Medicine' : 'Add New Medicine'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold p-1 rounded-lg transition"
          >
            &times;
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="mt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <CustomInput label="Name of Medicine *" name="nameOfMedicine" type="text" value={formData.nameOfMedicine} onChange={handleInputChange} placeholder="Enter Name of Medicine" error={errors.nameOfMedicine} />
            <CustomInput label="Company" name="company" type="text" value={formData.company} onChange={handleInputChange} placeholder="Enter Company" />
            <CustomInput label="Potency or Power" name="potencyOrPower" type="text" value={formData.potencyOrPower} onChange={handleInputChange} placeholder="Enter Potency or Power" />
            <CustomInput label="Quantity *" name="quantity" type="number" value={formData.quantity} onChange={handleInputChange} placeholder="Enter Quantity" error={errors.quantity} />
            <CustomInput label="Type of Medicine" name="typeOfMedicine" type="text" value={formData.typeOfMedicine} onChange={handleInputChange} placeholder="Enter Type of Medicine" />
            <CustomInput label="Distributor Name" name="distributorName" type="text" value={formData.distributorName} onChange={handleInputChange} placeholder="Enter Distributor Name" />
            <CustomInput label="Package (ml)" name="package" type="number" value={formData.package} onChange={handleInputChange} placeholder="Enter Package" error={errors.package} />
            <CustomInput label="Packaging Size" name="packagingSize" type="text" value={formData.packagingSize} onChange={handleInputChange} placeholder="e.g. 10×10, 1×30" />
            <CustomInput label="Expiry Date" name="expiryDate" type="date" value={formData.expiryDate} onChange={handleInputChange} />
            <CustomInput label="Mfg. Date" name="mafDate" type="date" value={formData.mafDate} onChange={handleInputChange} />
            <CustomInput label="Order ID" name="orderId" type="text" value={formData.orderId} onChange={handleInputChange} placeholder="Distributor's order ID" />
            <CustomInput label="Invoice No." name="invoiceNo" type="text" value={formData.invoiceNo} onChange={handleInputChange} placeholder="Distributor's invoice no." />
            <CustomInput label="Minimum Quantity" name="minQuantity" type="number" value={formData.minQuantity} onChange={handleInputChange} placeholder="Enter Minimum Quantity" error={errors.minQuantity} />
            <CustomInput label="Maximum Quantity" name="maxQuantity" type="number" value={formData.maxQuantity} onChange={handleInputChange} placeholder="Enter Maximum Quantity" error={errors.maxQuantity} />
            <CustomInput label="MRP" name="mrp" type="number" value={formData.mrp} onChange={handleInputChange} placeholder="Enter MRP" error={errors.mrp} />
            <CustomInput label="Discount" name="discount" type="number" value={formData.discount} onChange={handleInputChange} placeholder="Enter Discount" error={errors.discount} />
            <CustomInput label="HSN Code" name="hsnCode" type="text" value={formData.hsnCode} onChange={handleInputChange} placeholder="Enter HSN Code" />
            <CustomInput label="GST %" name="gst" type="number" value={formData.gst} onChange={handleInputChange} placeholder="Enter GST %" error={errors.gst} />
          </div>

          {/* Actions */}
          <div className="mt-8 flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-gray-100 px-6 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing}
              className="rounded-lg bg-blue-600 px-8 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition disabled:opacity-50"
            >
              {isProcessing ? 'Saving...' : (formData?._id ? 'Update Medicine' : 'Add Medicine')}
            </button>
          </div>
        </form>
      </div>
    </div></ModalPortal>
  );
}
